import { useState, useEffect, useCallback, useMemo } from 'react'
import { 
  Activity, Search, Filter, RefreshCw, Clock, User, Server,
  FileText, ChevronDown, ChevronUp, Trash2,
  Edit, Plus, Eye, Settings, Download, Send, CreditCard,
  Ticket, Users, Cpu, RotateCcw, Power, Terminal, Zap
} from 'lucide-react'
import { adminApi } from '../services/ApiService'
import './AuditHub.css'

const ACTION_ICONS = {
  create: Plus, update: Edit, delete: Trash2, view: Eye,
  send: Send, pay: CreditCard, login: User, logout: User,
  command: Server, config: Settings, export: Download,
  comment: FileText, generate: RefreshCw, default: Activity
}

const ACTION_COLORS = {
  create: '#10b981', update: '#3b82f6', delete: '#ef4444',
  view: '#6b7280', send: '#8b5cf6', pay: '#f59e0b',
  login: '#10b981', logout: '#6b7280', command: '#f97316',
  config: '#6366f1', export: '#06b6d4', comment: '#ec4899',
  generate: '#14b8a6', default: '#6b7280'
}

const ACTION_LABELS = {
  create: 'Crear', update: 'Actualizar', delete: 'Eliminar',
  view: 'Ver', send: 'Enviar', pay: 'Pago',
  login: 'Login', logout: 'Logout', command: 'Comando',
  config: 'Configuración', export: 'Exportar', comment: 'Comentario',
  generate: 'Generar'
}

const ENTITY_LABELS = {
  ticket: 'Ticket', invoice: 'Factura', quote: 'Presupuesto',
  'credit-note': 'Nota Crédito', receipt: 'Recibo', recurring: 'Recurrente',
  product: 'Producto', component: 'Componente', supplier: 'Proveedor',
  'purchase-order': 'Orden Compra', device: 'Dispositivo', client: 'Cliente',
  'admin-user': 'Admin', subscription: 'Suscripción', activity: 'Actividad',
  deal: 'Deal', goal: 'Meta', referral: 'Referido', template: 'Template',
  warranty: 'Garantía', note: 'Nota', tax: 'Config Fiscal',
  afip: 'AFIP', suggestion: 'Sugerencia', system: 'Sistema',
  notification: 'Notificación'
}

function timeAgo(dateString) {
  const d = new Date(dateString)
  const now = new Date()
  const diffMs = now - d
  const mins = Math.floor(diffMs / 60000)
  if (mins < 1) return 'Ahora'
  if (mins < 60) return `Hace ${mins}m`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `Hace ${hrs}h`
  const days = Math.floor(hrs / 24)
  if (days < 7) return `Hace ${days}d`
  return d.toLocaleDateString('es-AR')
}

export default function AuditHub() {
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [actionFilter, setActionFilter] = useState('')
  const [entityFilter, setEntityFilter] = useState('')
  const [userFilter, setUserFilter] = useState('')
  const [expandedId, setExpandedId] = useState(null)
  const [stats, setStats] = useState(null)

  const loadLogs = useCallback(async () => {
    setLoading(true)
    try {
      const params = {}
      if (actionFilter) params.action = actionFilter
      if (entityFilter) params.entity = entityFilter
      if (userFilter) params.user = userFilter
      if (search) params.search = search
      const res = await adminApi.get('/admin/audit-logs', params)
      setLogs(res.logs || [])
      setStats(res.stats || null)
    } catch (e) {
      console.error('loadAuditLogs:', e)
    } finally { setLoading(false) }
  }, [actionFilter, entityFilter, userFilter, search])

  useEffect(() => { loadLogs() }, [loadLogs])

  const uniqueUsers = useMemo(() => {
    const users = new Set(logs.map(l => l.userName).filter(Boolean))
    return [...users].sort()
  }, [logs])

  const uniqueEntities = useMemo(() => {
    const entities = new Set(logs.map(l => l.entity).filter(Boolean))
    return [...entities].sort()
  }, [logs])

  const uniqueActions = useMemo(() => {
    const actions = new Set(logs.map(l => l.action).filter(Boolean))
    return [...actions].sort()
  }, [logs])

  const filteredLogs = useMemo(() => {
    if (!search) return logs
    const q = search.toLowerCase()
    return logs.filter(l =>
      (l.description || '').toLowerCase().includes(q) ||
      (l.userName || '').toLowerCase().includes(q) ||
      (l.entity || '').toLowerCase().includes(q) ||
      (l.entityId || '').toLowerCase().includes(q)
    )
  }, [logs, search])

  return (
    <div className="audit-page">
      <div className="audit-header">
        <div className="audit-title-row">
          <Activity size={28} />
          <div>
            <h1>Registro de Actividad</h1>
            <p className="audit-subtitle">{filteredLogs.length} registros</p>
          </div>
        </div>
        <button className="audit-btn primary" onClick={loadLogs} disabled={loading}>
          <RefreshCw size={16} className={loading ? 'spin' : ''} /> Actualizar
        </button>
      </div>

      {stats && (
        <div className="audit-stats">
          <div className="audit-stat"><span className="audit-stat-value">{stats.today || 0}</span><span className="audit-stat-label">Hoy</span></div>
          <div className="audit-stat"><span className="audit-stat-value">{stats.thisWeek || 0}</span><span className="audit-stat-label">Esta Semana</span></div>
          <div className="audit-stat"><span className="audit-stat-value">{stats.thisMonth || 0}</span><span className="audit-stat-label">Este Mes</span></div>
          <div className="audit-stat"><span className="audit-stat-value">{stats.totalUsers || 0}</span><span className="audit-stat-label">Usuarios</span></div>
        </div>
      )}

      <div className="audit-filters">
        <div className="audit-search">
          <Search size={16} />
          <input placeholder="Buscar en logs..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select value={actionFilter} onChange={e => { setActionFilter(e.target.value) }}>
          <option value="">Todas las acciones</option>
          {uniqueActions.map(a => <option key={a} value={a}>{ACTION_LABELS[a] || a}</option>)}
        </select>
        <select value={entityFilter} onChange={e => { setEntityFilter(e.target.value) }}>
          <option value="">Todas las entidades</option>
          {uniqueEntities.map(e => <option key={e} value={e}>{ENTITY_LABELS[e] || e}</option>)}
        </select>
        <select value={userFilter} onChange={e => { setUserFilter(e.target.value) }}>
          <option value="">Todos los usuarios</option>
          {uniqueUsers.map(u => <option key={u} value={u}>{u}</option>)}
        </select>
      </div>

      <div className="audit-timeline">
        {loading && <div className="audit-loading"><RefreshCw size={20} className="spin" /> Cargando...</div>}
        {!loading && filteredLogs.length === 0 && (
          <div className="audit-empty"><Activity size={40} /><p>No hay registros de actividad</p></div>
        )}
        {filteredLogs.map(log => {
          const IconComponent = ACTION_ICONS[log.action] || ACTION_ICONS.default
          const color = ACTION_COLORS[log.action] || ACTION_COLORS.default
          const isExpanded = expandedId === log.$id
          return (
            <div key={log.$id} className={`audit-entry ${isExpanded ? 'expanded' : ''}`} onClick={() => setExpandedId(isExpanded ? null : log.$id)}>
              <div className="audit-entry-icon" style={{ background: `${color}20`, color }}>
                <IconComponent size={16} />
              </div>
              <div className="audit-entry-content">
                <div className="audit-entry-main">
                  <span className="audit-entry-desc">{log.description}</span>
                  <span className="audit-entry-time"><Clock size={12} /> {timeAgo(log.createdAt)}</span>
                </div>
                <div className="audit-entry-meta">
                  <span className="audit-tag action" style={{ background: `${color}20`, color }}>{ACTION_LABELS[log.action] || log.action}</span>
                  <span className="audit-tag entity">{ENTITY_LABELS[log.entity] || log.entity}</span>
                  {log.entity === 'device' && log.entityId && (
                    <span className="audit-tag device-mac"><Cpu size={12} /> {log.entityId}</span>
                  )}
                  {log.userName && <span className="audit-tag user"><User size={12} /> {log.userName}</span>}
                </div>
                {isExpanded && log.details && (
                  <div className="audit-entry-details">
                    <pre>{(() => {
                      try {
                        const parsed = typeof log.details === 'string' ? JSON.parse(log.details) : log.details
                        return JSON.stringify(parsed, null, 2)
                      } catch { return log.details }
                    })()}</pre>
                  </div>
                )}
              </div>
              <div className="audit-entry-chevron">
                {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
